import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ProviderErrorBoundary } from './ProviderErrorBoundary';
import './ProviderShell.css';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const MAX_CONCURRENT_IFRAMES = 5;
const HANDSHAKE_TIMEOUT_MS = 15_000; // 15s — matches iframe-context.md §12
const HANDSHAKE_PROTOCOL_VERSION = '1';
const MSG_NAVIGATE_TO = 'nurturecrm:navigate_to';
const JWT_REFRESH_THRESHOLD_MS = 8 * 60 * 1000; // 8 min (80% of 10-min JWT)

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/**
 * ProviderShell — manages multiple module provider iframes with:
 *  - Lazy loading (iframe created only on first tab access)
 *  - Keep-alive on tab switch (hidden via CSS, not unmounted)
 *  - Idle timeout destruction (30 min default)
 *  - LRU eviction when MAX_CONCURRENT_IFRAMES is exceeded
 *  - Deep linking via the `navigateToPath` prop (e.g. "/ats/jobs/123")
 *  - Per-provider error boundaries
 *  - postMessage handshake protocol (nurturecrm:handshake → context)
 *  - Parent→iframe navigation via nurturecrm:navigate_to postMessage
 *  - Proactive JWT refresh at 80% of token lifetime
 *  - Loading + JWT exchange indicators
 *
 * Props:
 *   providers {Array<{id, name, url, icon?}>}  Installed providers.
 *                                               If omitted, fetched from the API.
 *   activeProviderId {string}                   Initially active provider (controlled-ish).
 *   navigateToPath {string|null}               Path to navigate the active provider to.
 *   navigateToNonce {number}                    Increments to trigger re-navigation.
 *   onNavigate {func}                           Called when iframe requests parent nav.
 *   idleTimeoutMs {number}                      Override the idle destroy timeout.
 *   maxConcurrent {number}                      Override the concurrent iframe cap.
 */
export function ProviderShell({
    providers: injectedProviders,
    activeProviderId: initialActive,
    navigateToPath,
    navigateToNonce = 0,
    onNavigate,
    idleTimeoutMs = IDLE_TIMEOUT_MS,
    maxConcurrent = MAX_CONCURRENT_IFRAMES,
}) {
    const [providers, setProviders] = useState(injectedProviders || []);
    const [providersLoading, setProvidersLoading] = useState(!injectedProviders);
    const [providersError, setProvidersError] = useState(null);

    const [activeProvider, setActiveProvider] = useState(initialActive || null);
    // {providerId: {loaded, error, lastAccessed, token, handshakeComplete, deepLink}}
    const [iframeStates, setIframeStates] = useState({});
    const [loadingTokens, setLoadingTokens] = useState({});
    const [deepLinks, setDeepLinks] = useState({});  // per-provider deep links

    const iframeRefs = useRef({});
    const idleTimers = useRef({});
    const handshakeTimers = useRef({});
    const refreshTimers = useRef({});  // proactive JWT refresh timers
    const pendingChallenges = useRef({}); // {providerId: challengeNonce}
    const messageHandlersBound = useRef(false);

    // ── Fetch installed providers from the API (Issue #141 endpoint) ──────
    useEffect(() => {
        if (injectedProviders && injectedProviders.length > 0) return;

        let cancelled = false;
        (async () => {
            setProvidersLoading(true);
            try {
                const token = localStorage.getItem('crm_token');
                const res = await fetch(`${API_BASE}/v1/modules/providers`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) throw new Error(`Failed to load providers (${res.status})`);
                const data = await res.json();
                const list = Array.isArray(data) ? data : data.providers || [];
                if (cancelled) return;
                setProviders(list);
                // Auto-select the first provider if none is active
                if (!activeProvider && list.length > 0) {
                    setActiveProvider(list[0].id);
                }
            } catch (err) {
                if (!cancelled) setProvidersError(err.message);
            } finally {
                if (!cancelled) setProvidersLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Fetch context JWT for a provider (Issue #141 endpoint) ────────────
    const fetchContextToken = useCallback(async (providerId) => {
        setLoadingTokens((prev) => ({ ...prev, [providerId]: true }));
        try {
            const token = localStorage.getItem('crm_token');
            const response = await fetch(
                `${API_BASE}/v1/modules/providers/${providerId}/iframe-context`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                }
            );
            if (!response.ok) {
                throw new Error(`Failed to fetch context token: ${response.status}`);
            }
            const data = await response.json();
            return data.context_token;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`[ProviderShell] Error fetching context token for ${providerId}:`, err);
            throw err;
        } finally {
            setLoadingTokens((prev) => ({ ...prev, [providerId]: false }));
        }
    }, []);

    // ── Destroy an iframe (eviction / idle timeout) ───────────────────────
    const destroyIframe = useCallback((providerId) => {
        // Clear timers
        if (idleTimers.current[providerId]) {
            clearTimeout(idleTimers.current[providerId]);
            delete idleTimers.current[providerId];
        }
        if (handshakeTimers.current[providerId]) {
            clearTimeout(handshakeTimers.current[providerId]);
            delete handshakeTimers.current[providerId];
        }
        if (refreshTimers.current[providerId]) {
            clearTimeout(refreshTimers.current[providerId]);
            delete refreshTimers.current[providerId];
        }
        delete pendingChallenges.current[providerId];
        // Remove the ref so React unmounts the iframe element
        if (iframeRefs.current[providerId]) {
            delete iframeRefs.current[providerId];
        }
        setIframeStates((prev) => {
            if (!prev[providerId]) return prev;
            const next = { ...prev };
            delete next[providerId];
            return next;
        });
    }, []);

    // ── Reset the idle destroy timer for a provider ───────────────────────
    const resetIdleTimer = useCallback((providerId) => {
        if (idleTimers.current[providerId]) {
            clearTimeout(idleTimers.current[providerId]);
        }
        idleTimers.current[providerId] = setTimeout(() => {
            destroyIframe(providerId);
        }, idleTimeoutMs);
    }, [destroyIframe, idleTimeoutMs]);

    // ── Send the context JWT to the iframe via postMessage ────────────────
    const sendContextToIframe = useCallback((providerId, token) => {
        const provider = providers.find((p) => p.id === providerId);
        if (!provider) return;
        const iframe = iframeRefs.current[providerId];
        if (!iframe || !iframe.contentWindow) return;

        // Derive the target origin from the provider URL for security.
        // postMessage with a specific targetOrigin prevents token leakage
        // to a spoofed iframe origin (see iframe-context.md §11).
        let targetOrigin;
        try {
            targetOrigin = new URL(provider.url).origin;
        } catch {
            targetOrigin = provider.url; // best-effort fallback
        }

        iframe.contentWindow.postMessage(
            {
                type: 'nurturecrm:context',
                token,
            },
            targetOrigin
        );
    }, [providers]);

    // ── Proactive JWT refresh at 80% of token lifetime ────────────────────
    const proactiveRefreshContext = useCallback((providerId) => {
        const state = iframeStates[providerId];
        const provider = providers.find((p) => p.id === providerId);
        if (!state?.handshakeComplete || !state?.loaded || !provider) return;

        console.log(`[ProviderShell] Proactive JWT refresh for ${providerId}`);

        fetchContextToken(providerId)
            .then((token) => {
                sendContextToIframe(providerId, token);
                setIframeStates((prev) => ({
                    ...prev,
                    [providerId]: {
                        ...(prev[providerId] || {}),
                        token,
                        lastAccessed: Date.now(),
                        refreshTimer: setTimeout(
                            () => proactiveRefreshContext(providerId),
                            JWT_REFRESH_THRESHOLD_MS,
                        ),
                    },
                }));
            })
            .catch((err) => {
                console.warn(`[ProviderShell] Proactive refresh failed for ${providerId}:`, err);
                // The reactive refresh_context mechanism will handle it on next 401
            });
    }, [iframeStates, providers, fetchContextToken, sendContextToIframe]);

    // ── Initiate the postMessage handshake with the iframe ────────────────
    const startHandshake = useCallback((providerId) => {
        const provider = providers.find((p) => p.id === providerId);
        if (!provider) return;
        const iframe = iframeRefs.current[providerId];
        if (!iframe || !iframe.contentWindow) return;

        let targetOrigin;
        try {
            targetOrigin = new URL(provider.url).origin;
        } catch {
            targetOrigin = provider.url;
        }

        // Generate a challenge nonce to prevent replay
        const challenge =
            typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random()}`;
        pendingChallenges.current[providerId] = challenge;

        iframe.contentWindow.postMessage(
            {
                type: 'nurturecrm:handshake',
                version: HANDSHAKE_PROTOCOL_VERSION,
                challenge,
            },
            targetOrigin
        );

        // Timeout: if the iframe doesn't ack within 15s, show an error
        if (handshakeTimers.current[providerId]) {
            clearTimeout(handshakeTimers.current[providerId]);
        }
        handshakeTimers.current[providerId] = setTimeout(() => {
            setIframeStates((prev) => ({
                ...prev,
                [providerId]: {
                    ...(prev[providerId] || {}),
                    error: 'Module is taking longer than expected to load.',
                    handshakeComplete: false,
                },
            }));
        }, HANDSHAKE_TIMEOUT_MS);
    }, [providers]);

    // ── Navigate the iframe to a specific path (parent → iframe) ──────────
    const handleNavigateTo = useCallback((providerId, path) => {
        const state = iframeStates[providerId];
        const provider = providers.find((p) => p.id === providerId);
        if (!provider) return;

        if (state?.handshakeComplete && state?.loaded) {
            // Iframe is live — send navigate_to via postMessage
            const iframe = iframeRefs.current[providerId];
            if (iframe?.contentWindow) {
                let targetOrigin;
                try {
                    targetOrigin = new URL(provider.url).origin;
                } catch {
                    targetOrigin = provider.url;
                }
                iframe.contentWindow.postMessage(
                    {
                        type: MSG_NAVIGATE_TO,
                        path,
                        timestamp: Date.now(),
                    },
                    targetOrigin,
                );
                console.log(`[ProviderShell] Sent navigate_to to ${providerId}: ${path}`);
            }
        } else {
            // Iframe not loaded — set deep link so it boots to this path
            setDeepLinks((prev) => ({ ...prev, [providerId]: path }));
            console.log(`[ProviderShell] Set deep link for ${providerId}: ${path}`);
        }
    }, [iframeStates, providers]);

    // ── Watch for navigation requests from sidebar ───────────────────────
    useEffect(() => {
        if (navigateToPath && navigateToNonce > 0) {
            handleNavigateTo(activeProvider, navigateToPath);
        }
    }, [navigateToNonce, navigateToPath, activeProvider, handleNavigateTo]);

    // ── Construct iframe src with query-param-based deep link ─────────────
    // IMPORTANT: Once the iframe is live (handshakeComplete), we must NOT
    // change the src — that would trigger a full browser reload and break
    // the handshake. Navigation for live iframes happens via postMessage.
    const getIframeSrc = useCallback((provider) => {
        const state = iframeStates[provider.id];
        // If the iframe is already live, return the base URL (no ?dl=)
        if (state?.handshakeComplete) {
            return provider.url;
        }
        // Only use deep link for initial load (before handshake completes)
        const dl = deepLinks[provider.id] || (provider.id === activeProvider ? navigateToPath : null);
        if (dl) {
            try {
                const url = new URL(provider.url);
                url.searchParams.set('dl', dl);
                return url.toString();
            } catch {
                return `${provider.url}?dl=${encodeURIComponent(dl)}`;
            }
        }
        return provider.url;
    }, [deepLinks, activeProvider, navigateToPath, iframeStates]);

    // ── Create a stable ref callback for each iframe ──────────────────────
    // Stores the DOM node and kicks off the handshake on mount. The handshake
    // is deferred slightly so the iframe has time to register its message
    // listener before we post.
    const createIframeRef = useCallback((providerId) => (el) => {
        if (el) {
            iframeRefs.current[providerId] = el;
            // Only start handshake if not already complete or in-flight
            const current = iframeStates[providerId];
            if (
                current &&
                !current.handshakeComplete &&
                !pendingChallenges.current[providerId]
            ) {
                setTimeout(() => startHandshake(providerId), 100);
            }
        } else {
            // React is unmounting the iframe — clean up the ref
            delete iframeRefs.current[providerId];
        }
    }, [iframeStates, startHandshake]);

    // ── Global postMessage listener (handles handshake_ack, refresh, nav) ─
    useEffect(() => {
        if (messageHandlersBound.current) return;
        messageHandlersBound.current = true;

        const handleMessage = async (event) => {
            const { data, origin } = event;
            if (!data || typeof data !== 'object') return;

            // Find which provider this message belongs to by matching origin
            const provider = providers.find((p) => {
                try {
                    return new URL(p.url).origin === origin;
                } catch {
                    return false;
                }
            });
            if (!provider) return; // ignore messages from unknown origins

            const providerId = provider.id;

            switch (data.type) {
                case 'nurturecrm:handshake_ack': {
                    // Verify the challenge nonce matches
                    const expectedChallenge = pendingChallenges.current[providerId];
                    if (!expectedChallenge || data.challenge !== expectedChallenge) {
                        return; // ignore mismatched challenge
                    }
                    delete pendingChallenges.current[providerId];
                    if (handshakeTimers.current[providerId]) {
                        clearTimeout(handshakeTimers.current[providerId]);
                        delete handshakeTimers.current[providerId];
                    }
                    // Handshake complete — now deliver the context JWT
                    try {
                        const token = await fetchContextToken(providerId);
                        sendContextToIframe(providerId, token);
                        setIframeStates((prev) => ({
                            ...prev,
                            [providerId]: {
                                ...(prev[providerId] || {}),
                                loaded: true,
                                error: null,
                                token,
                                handshakeComplete: true,
                                lastAccessed: Date.now(),
                            },
                        }));
                        resetIdleTimer(providerId);
                        // Schedule proactive JWT refresh at 80% of token lifetime
                        if (refreshTimers.current[providerId]) {
                            clearTimeout(refreshTimers.current[providerId]);
                        }
                        refreshTimers.current[providerId] = setTimeout(
                            () => proactiveRefreshContext(providerId),
                            JWT_REFRESH_THRESHOLD_MS,
                        );
                    } catch (err) {
                        setIframeStates((prev) => ({
                            ...prev,
                            [providerId]: {
                                ...(prev[providerId] || {}),
                                loaded: false,
                                error: err.message,
                                token: null,
                                handshakeComplete: false,
                                lastAccessed: Date.now(),
                            },
                        }));
                    }
                    break;
                }

                case 'nurturecrm:refresh_context': {
                    // Provider requests a fresh context JWT
                    try {
                        const token = await fetchContextToken(providerId);
                        sendContextToIframe(providerId, token);
                        setIframeStates((prev) => ({
                            ...prev,
                            [providerId]: {
                                ...(prev[providerId] || {}),
                                token,
                                lastAccessed: Date.now(),
                            },
                        }));
                        resetIdleTimer(providerId);
                    } catch (err) {
                        // Send context_error to the iframe
                        const iframe = iframeRefs.current[providerId];
                        if (iframe?.contentWindow) {
                            iframe.contentWindow.postMessage(
                                {
                                    type: 'nurturecrm:context_error',
                                    error: 'refresh_failed',
                                    message: err.message,
                                },
                                origin
                            );
                        }
                    }
                    break;
                }

                case 'nurturecrm:navigate': {
                    // Iframe requests parent navigation
                    if (typeof data.path === 'string' && data.path.startsWith('/')) {
                        if (typeof onNavigate === 'function') {
                            onNavigate(data.path);
                        }
                    }
                    break;
                }

                case 'nurturecrm:resize': {
                    // Iframe requests height adjustment
                    const iframe = iframeRefs.current[providerId];
                    if (iframe && typeof data.height === 'number') {
                        const clamped = Math.min(Math.max(data.height, 150), 2000);
                        iframe.style.height = `${clamped}px`;
                    }
                    break;
                }

                default:
                    break;
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
            messageHandlersBound.current = false;
        };
    }, [providers, fetchContextToken, sendContextToIframe, resetIdleTimer, onNavigate]);

    // ── Initialize (lazy-load) a provider iframe ──────────────────────────
    const initProvider = useCallback(async (providerId) => {
        setIframeStates((prev) => {
            if (prev[providerId]?.loaded) {
                // Already loaded — just bump lastAccessed + reset timer
                resetIdleTimer(providerId);
                return {
                    ...prev,
                    [providerId]: { ...prev[providerId], lastAccessed: Date.now() },
                };
            }
            return prev;
        });

        if (iframeStates[providerId]?.loaded) return;

        // Enforce max concurrent iframes via LRU eviction
        const loadedCount = Object.values(iframeStates).filter((s) => s.loaded).length;
        if (loadedCount >= maxConcurrent) {
            const lru = Object.entries(iframeStates)
                .filter(([, s]) => s.loaded)
                .sort((a, b) => a[1].lastAccessed - b[1].lastAccessed)[0];
            if (lru) {
                destroyIframe(lru[0]);
            }
        }

        // Mark as loading (iframe element will mount, triggering handshake)
        setIframeStates((prev) => ({
            ...prev,
            [providerId]: {
                loaded: false,
                error: null,
                lastAccessed: Date.now(),
                token: null,
                handshakeComplete: false,
                deepLink: navigateToPath || null,
            },
        }));
    }, [iframeStates, maxConcurrent, navigateToPath, resetIdleTimer, destroyIframe]);

    // ── Auto-initialize the active provider on mount ──────────────────────
    // Ensures the iframe loads immediately without requiring a tab click.
    useEffect(() => {
        if (activeProvider && !iframeStates[activeProvider]) {
            initProvider(activeProvider);
        }
    }, [activeProvider, iframeStates, initProvider]);

    // ── Handle tab click ──────────────────────────────────────────────────
    const handleTabClick = useCallback((providerId) => {
        setActiveProvider(providerId);
        initProvider(providerId);
    }, [initProvider]);

    // ── Retry handler for error boundary ──────────────────────────────────
    const handleRetry = useCallback((providerId) => {
        destroyIframe(providerId);
        // Re-init after a tick so the iframe element is fully unmounted first
        setTimeout(() => initProvider(providerId), 50);
    }, [destroyIframe, initProvider]);

    // ── Cleanup all timers on unmount ─────────────────────────────────────
    useEffect(() => {
        return () => {
            Object.values(idleTimers.current).forEach(clearTimeout);
            Object.values(handshakeTimers.current).forEach(clearTimeout);
            Object.values(refreshTimers.current).forEach(clearTimeout);
        };
    }, []);

    // ── Render: loading state for provider list ───────────────────────────
    if (providersLoading) {
        return (
            <div className="provider-shell">
                <div className="provider-shell-loading">
                    <div className="provider-shell-spinner" />
                    <p>Loading modules…</p>
                </div>
            </div>
        );
    }

    if (providersError) {
        return (
            <div className="provider-shell">
                <div className="provider-error">
                    <h3 className="provider-error-title">Modules unavailable</h3>
                    <p className="provider-error-message">{providersError}</p>
                    <div className="provider-error-actions">
                        <button
                            className="provider-error-btn provider-error-btn-primary"
                            onClick={() => window.location.reload()}
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!providers || providers.length === 0) {
        return (
            <div className="provider-shell">
                <div className="provider-shell-empty">
                    <p>No modules installed.</p>
                </div>
            </div>
        );
    }

    // ── Render: tab bar + iframe panes ────────────────────────────────────
    return (
        <div className="provider-shell">
            {/* Tab bar — only shown when multiple providers */}
            {providers.length > 1 && (
            <div className="provider-shell-tabs" role="tablist">
                {providers.map((provider) => {
                    const isActive = activeProvider === provider.id;
                    const state = iframeStates[provider.id];
                    const isLoadingToken = loadingTokens[provider.id];
                    return (
                        <button
                            key={provider.id}
                            role="tab"
                            aria-selected={isActive}
                            className={`provider-shell-tab ${isActive ? 'provider-shell-tab-active' : ''}`}
                            onClick={() => handleTabClick(provider.id)}
                        >
                            {provider.icon && (() => {
                                const Icon = provider.icon;
                                return (
                                    <span className="provider-shell-tab-icon">
                                        <Icon size={16} />
                                    </span>
                                );
                            })()}
                            <span className="provider-shell-tab-label">{provider.name}</span>
                            {isLoadingToken && (
                                <span
                                    className="provider-shell-tab-badge provider-shell-tab-badge-loading"
                                    title="Exchanging context token…"
                                />
                            )}
                            {state?.error && !isLoadingToken && (
                                <span
                                    className="provider-shell-tab-badge provider-shell-tab-badge-error"
                                    title="Error loading module"
                                />
                            )}
                        </button>
                    );
                })}
            </div>
            )}

            {/* Iframe panes — kept alive (hidden) when switching tabs */}
            <div className="provider-shell-content">
                {providers.map((provider) => {
                    const state = iframeStates[provider.id];
                    const isActive = activeProvider === provider.id;
                    const providerUrl = getIframeSrc(provider);

                    // Only render the iframe element once the provider has been
                    // activated at least once (lazy loading).
                    if (!state) return null;

                    return (
                        <ProviderErrorBoundary
                            key={provider.id}
                            providerId={provider.id}
                            providerName={provider.name}
                            onRetry={() => handleRetry(provider.id)}
                        >
                            <div
                                className={`provider-shell-pane ${isActive ? 'provider-shell-pane-active' : ''}`}
                                style={{ display: isActive ? 'block' : 'none' }}
                            >
                                {/* Loading skeleton / JWT exchange indicator */}
                                {!state.handshakeComplete && !state.error && (
                                    <div className="provider-shell-pane-loading">
                                        <div className="provider-shell-spinner" />
                                        <p>
                                            {loadingTokens[provider.id]
                                                ? 'Exchanging secure context token…'
                                                : 'Connecting to module…'}
                                        </p>
                                    </div>
                                )}

                                {/* Error state (token fetch / handshake failure) */}
                                {state.error && (
                                    <div className="provider-error">
                                        <div className="provider-error-icon">
                                            <svg
                                                width="48"
                                                height="48"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                                <line x1="12" y1="9" x2="12" y2="13" />
                                                <line x1="12" y1="17" x2="12.01" y2="17" />
                                            </svg>
                                        </div>
                                        <h3 className="provider-error-title">
                                            Unable to load {provider.name}
                                        </h3>
                                        <p className="provider-error-message">{state.error}</p>
                                        <div className="provider-error-actions">
                                            <button
                                                className="provider-error-btn provider-error-btn-primary"
                                                onClick={() => handleRetry(provider.id)}
                                            >
                                                Retry
                                            </button>
                                            <button
                                                className="provider-error-btn provider-error-btn-secondary"
                                                onClick={() => {
                                                    window.location.href = 'mailto:support@nurturecrm.ai';
                                                }}
                                            >
                                                Contact Support
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* The iframe — sandboxed per iframe-context.md §10 */}
                                {!state.error && (
                                    <iframe
                                        ref={createIframeRef(provider.id)}
                                        src={providerUrl}
                                        title={provider.name}
                                        className="provider-shell-iframe"
                                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
                                        referrerPolicy="no-referrer"
                                        loading="lazy"
                                        onLoad={() => {
                                            // Bump lastAccessed + reset idle timer on load.
                                            // NOTE: Do NOT re-initiate handshake here.
                                            // bootDashboard() in the Sites API modifies the
                                            // iframe's document (creates an inner iframe),
                                            // which fires load on the outer iframe. Re-handshaking
                                            // would create an infinite loop. The src never
                                            // changes for live iframes (getIframeSrc returns
                                            // the base URL when handshakeComplete is true), so
                                            // there's no legitimate reload to detect.
                                            resetIdleTimer(provider.id);
                                        }}
                                    />
                                )}
                            </div>
                        </ProviderErrorBoundary>
                    );
                })}
            </div>
        </div>
    );
}

export default ProviderShell;
